"use client"

import { OutlineToPrimaryButtonProps } from "@/types/types"
import { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"

export const OutlineToPrimaryButton = ({
  buttonName,
  buttonLink,
  buttonBg = "#18AF4E",
  index,
  buttonStyle,
  // Backward compatibility - giữ lại các props cũ
  buttonSize: buttonSizeProp,
  buttonFontSize: buttonFontSizeProp,
  buttonFontWeight: buttonFontWeightProp,
  buttonLineHeight: buttonLineHeightProp,
  hoverBackgroundColor: hoverBackgroundColorProp,
  hoverColor: hoverColorProp,
  hoverBorder: hoverBorderProp,
  hoverTransform: hoverTransformProp,
  hoverOpacity: hoverOpacityProp,
  hoverBoxShadow: hoverBoxShadowProp,
}: OutlineToPrimaryButtonProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const [borderOpacity, setBorderOpacity] = useState(0)
  const buttonId = `outline-primary-button-${index}-${Math.random().toString(36).substr(2, 9)}`

  useEffect(() => {
    if (isHovered) {
      setBorderOpacity(1)
      const timer = setTimeout(() => {
        setBorderOpacity(0)
      }, 200)
      return () => clearTimeout(timer)
    } else {
      setBorderOpacity(0)
    }
  }, [isHovered])

  // Ưu tiên sử dụng buttonStyle object, fallback về các props riêng lẻ
  const buttonSize = buttonStyle?.buttonSize || buttonSizeProp
  const buttonFontSize = buttonStyle?.buttonFontSize || buttonFontSizeProp
  const buttonFontWeight = buttonStyle?.buttonFontWeight || buttonFontWeightProp
  const buttonLineHeight = buttonStyle?.buttonLineHeight || buttonLineHeightProp
  const hoverBackgroundColor = buttonStyle?.hoverBackgroundColor || hoverBackgroundColorProp
  const hoverColor = buttonStyle?.hoverColor || hoverColorProp
  const hoverBorder = buttonStyle?.hoverBorder || hoverBorderProp
  const hoverTransform = buttonStyle?.hoverTransform || hoverTransformProp
  const hoverOpacity = buttonStyle?.hoverOpacity || hoverOpacityProp
  const hoverBoxShadow = buttonStyle?.hoverBoxShadow || hoverBoxShadowProp

  const baseStyle: React.CSSProperties = {
    position: "relative",
    color: "#fff",
    borderRadius: "4px",
    fontWeight: buttonFontWeight ? parseInt(buttonFontWeight) : 700,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #fff",
    background: "transparent",
    transition:
      "filter 0.3s ease, border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease, transform 0.3s ease, opacity 0.3s ease, box-shadow 0.3s ease",
    cursor: "pointer",
    overflow: "visible",
    zIndex: 2,
    ...(buttonSize?.width && { width: buttonSize.width }),
    ...(buttonSize?.height && { height: buttonSize.height }),
    ...(buttonFontSize && { fontSize: buttonFontSize }),
    ...(buttonLineHeight && { lineHeight: buttonLineHeight }),
  }

  const hoverStyle: React.CSSProperties = {
    ...baseStyle,
    background: hoverBackgroundColor || buttonBg,
    color: hoverColor || baseStyle.color,
    border: hoverBorder || "1px solid transparent",
    transform: hoverTransform || undefined,
    opacity: hoverOpacity ? parseFloat(hoverOpacity) : undefined,
    boxShadow: hoverBoxShadow || undefined,
  }

  const handleClick = () => {
    if (buttonLink) {
      window.location.href = buttonLink
    }
  }

  return (
    <>
      <span
        id={buttonId}
        onClick={handleClick}
        data-aos="aos-fade-in-up"
        data-aos-duration="1000"
        data-aos-delay={`${500 + index * 100}`}
        className={twMerge(
          !buttonSize?.width && "w-[114px] sm:w-[123px]",
          !buttonSize?.height && "h-[46px]",
          !buttonFontSize && "text-sm sm:text-base",
          !buttonLineHeight && "leading-[46px] sm:leading-[46px]",
        )}
        style={isHovered ? hoverStyle : baseStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {buttonName}
        <span
          className={twMerge(
            isHovered ? "aos-animate" : "",
            "absolute top-1/2 left-1/2 w-[136.8px] h-[55.2px] sm:w-[124.6px] sm:h-[55.2px]",
          )}
          data-aos="aos-fade-in-up"
          data-aos-duration="800"
          style={{
            border: "2px solid #fff",
            borderRadius: "4px",
            pointerEvents: "none",
            opacity: borderOpacity,
            zIndex: -2,
            boxSizing: "border-box",
            transformOrigin: "center center",
            transform: isHovered ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.8333)",
            transition: "transform 0.6s cubic-bezier(0.6, 0, 0.1, 1), opacity 0.3s ease",
          }}
        />
        <span
          className={twMerge(
            isHovered ? "aos-animate" : "",
            "absolute top-1/2 left-1/2 w-[114px] h-[46px] sm:w-[123px] sm:h-[46px]",
          )}
          data-aos="aos-fade-in-up"
          data-aos-duration="800"
          style={{
            background: buttonBg,
            borderRadius: "4px",
            pointerEvents: "none",
            opacity: isHovered ? 1 : 0,
            zIndex: -1,
            boxSizing: "border-box",
            transformOrigin: "center center",
            transform: isHovered ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0.5)",
            transition: "transform 0.6s cubic-bezier(0.6, 0, 0.1, 1), opacity 0.6s cubic-bezier(0.6, 0, 0.1, 1)",
          }}
        />
      </span>
    </>
  )
}
